<!-- TPOD-2263: centred loading overlay while a language switch applies (the
     zh-CN translation file is fetched over HTTP). Mirrors the auto-enrol overlay. -->
@if (isChangingLanguage) {
    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 pointer-events-auto">
        <div class="bg-white rounded-lg px-6 py-5 shadow-lg flex flex-col items-center gap-3 min-w-[160px]">
            <mat-progress-spinner mode="indeterminate" [diameter]="40"></mat-progress-spinner>
            <span class="text-sm font-medium text-[#231F20]">{{ 'text.processing' | translate }}</span>
        </div>
    </div>
}

<header>
    <div class="max-width-container xl:px-[100px] md:px-[6.9%]">
        <div class="flex justify-between items-center md:px-0 px-6">
            <div
                class="left-panel lg:flex lg:items-center items-start"
                [ngClass]="[isAuthLayout ? 'w-1/5' : 'w-auto lg:w-3/5']">
                <div class="brand-logo-wrapper my-4">
                    <a [routerLink]="[isAuthLayout ? '/' : '/home']">
                        <img class="w-[5.3rem] md:w-[8rem]" src="./assets/images/brand_logo.png" alt="TaxPod-logo" />
                    </a>
                </div>
                <div *ngIf="!isAuthLayout && !isMobileView" class="lg:block hidden search-bar w-full mx-8 relative">
                    <div class="form-control-wrapper flex items-center">
                        <mat-icon
                            svgIcon="search"
                            class="absolute left-3 !w-4 text-[#004B65] cursor-pointer"
                            (click)="onSearch(searchInput.value)"></mat-icon>
                        <input
                            #searchInput
                            id="globalSearchBar"
                            type="text"
                            [attr.maxlength]="SEARCH_MAX_LENGTH"
                            class="form-control form-textbox !px-9 !h-11"
                            [placeholder]="'placeholder.defaultSearchExample' | translate"
                            (keydown.enter)="onSearch(searchInput.value)" />
                        <button
                            *ngIf="isSearchPopupVisible"
                            type="button"
                            class="flex items-center"
                            (click)="isSearchPopupVisible = false; onFilterClear()">
                            <mat-icon class="absolute right-3 text-[#a09d9d] !w-3" svgIcon="close"></mat-icon>
                        </button>
                    </div>

                    <div *ngIf="isSearchPopupVisible" #searchPopup class="absolute top-[calc(100%+0.5rem)] w-full">
                        <app-general-search-dropdown
                            (filterClearClicked)="onFilterClear()"
                            (suggestedKeywordClicked)="onSuggestedKeyword($event)"
                            (hotTopicClicked)="onHotTopicClicked()"></app-general-search-dropdown>
                    </div>
                </div>
            </div>
            <div class="right-panel lg:my-0 my-4" [ngClass]="[isAuthLayout ? 'w-4/5' : 'w-auto']">
                <div class="flex items-center justify-end gap-0.5">
                    <!-- isAskPodFeatureEnabled is the backend kill switch (SystemSetting
                         "Feature:AskPod:Web:Enabled"); it hides the entry point entirely. The
                         separate [disabled] binding below greys out a visible button mid-launch. -->
                    <button
                        *ngIf="!isAuthLayout && isAskPodFeatureEnabled"
                        type="button"
                        id="askPodLogoDesktop"
                        class="md:block hidden flex-shrink-0 relative overflow-visible"
                        aria-label="askPOD"
                        [matTooltip]="'askPodPopup.tooltipTaxMadeEasy' | translate"
                        matTooltipPosition="above"
                        [disabled]="isAskpodLoading || isAskpodDisabled"
                        (click)="onAskPodClicked()">
                        <img
                            class="h-7 w-auto"
                            src="./assets/images/askpod-logo.svg"
                            alt="askPOD" />
                        <span class="askpod-new-badge">{{ 'askPodPopup.betaBadge' | translate }}</span>
                    </button>
                    <span
                        *ngIf="!isAuthLayout && isAskPodFeatureEnabled"
                        class="nav-rule md:block hidden"
                        aria-hidden="true"></span>
                    <!-- TPOD-3318: prototype order is askPOD, language, calendar - the
                         calendar used to sit ahead of the switcher. Icons across this row are
                         now the prototype's 22px stroke SVGs drawn with currentColor, so they
                         follow their control into the hover colour. askPOD keeps its own logo. -->
                    <button
                        *ngIf="showLanguageDropdown"
                        type="button"
                        class="nav-pill flex-shrink-0"
                        [matMenuTriggerFor]="language">
                        <svg
                            class="nav-pill__icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true">
                            <circle cx="12" cy="12" r="9.25" />
                            <path d="M2.75 12h18.5" />
                            <path
                                d="M12 2.75a14.5 14.5 0 0 1 3.75 9.25A14.5 14.5 0 0 1 12 21.25a14.5 14.5 0 0 1-3.75-9.25A14.5 14.5 0 0 1 12 2.75z" />
                        </svg>
                        <span class="nav-pill__label">
                            @if (selectedLanguage) {
                                {{ getLanguageTranslation(selectedLanguage.toLocaleLowerCase()) | translate }}
                            }
                        </span>
                        <svg
                            class="nav-pill__chevron"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.25"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true">
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                    <!-- TPOD-3318: icon + "Calendar" label, matching the design prototype's
                         .tp-navpill. The icon is an inline stroke SVG rather than a background
                         image so it can inherit the pill's colour on hover; the label is hidden
                         below 768px, where this row is already crowded. Single link to the
                         webinar schedule - no dropdown. -->
                    <a
                        *ngIf="!isAuthLayout"
                        id="webinarScheduleNav"
                        class="nav-pill"
                        [routerLink]="['/program/schedule']">
                        <svg
                            class="nav-pill__icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true">
                            <rect x="3" y="4.5" width="18" height="16.5" rx="2.5" />
                            <path d="M16 2.5v4M8 2.5v4M3 10h18" />
                        </svg>
                        <span class="nav-pill__label">{{ 'menu.calendar' | translate }}</span>
                    </a>
                    @if (isAuthLayout && isUserAuthenticated()) {
                        <button
                            type="button"
                            class="btn-outline md:text-base text-sm text-error !px-3 flex items-center"
                            (click)="logout()">
                            <span class="inline-block bg-logout-icon icon-[1.5rem] mr-1.5"></span>
                            {{ 'menu.logout' | translate }}
                        </button>
                    }
                    <a
                        *ngIf="showDemoButton"
                        class="btn-outline-tertiary md:text-base text-xs"
                        href="https://taxpod.com.my/book-a-demo/"
                        appAddTargetBlankNoOpener>
                        {{ 'button.bookADemo' | translate }}
                    </a>
                    <mat-menu #language="matMenu">
                        <button
                            *ngFor="let language of availableLanguage"
                            type="button"
                            mat-menu-item
                            (click)="changeSelectedLanguage(language.formattedLocal)">
                            @if (language.languageName) {
                                {{ getLanguageTranslation(language.formattedLocal.toLocaleLowerCase()) | translate }}
                            }
                        </button>
                    </mat-menu>
                    @if (!isAuthLayout) {
                        <div class="md:relative">
                            <button #notificationButton id="notificationNav" type="button" class="nav-btn">
                                <svg
                                    class="nav-pill__icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.75"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    aria-hidden="true">
                                    <path d="M6.5 9a5.5 5.5 0 0 1 11 0c0 6 2.5 7.75 2.5 7.75H4S6.5 15 6.5 9" />
                                    <path d="M10.3 20.5a2 2 0 0 0 3.4 0" />
                                </svg>

                                <div class="count-notificaiton">{{ unreadNotificationCount }}</div>
                            </button>

                            <div
                                #notification
                                class="z-50 w-full md:w-[30rem] absolute right-0 top-16 md:top-[2.5rem]"
                                [ngClass]="isToggleNotification ? 'block' : 'hidden'">
                                <app-notification
                                    [isNotificationOpen]="isNotificationOpen"
                                    (closeNotificationDropdown)="triggerNotification()"></app-notification>
                            </div>
                        </div>
                        <button
                            #menuTrigger="matMenuTrigger"
                            id="myProfileNav"
                            type="button"
                            class="nav-btn"
                            [matMenuTriggerFor]="profile">
                            <svg
                                class="nav-pill__icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.75"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                aria-hidden="true">
                                <circle cx="12" cy="12" r="9.25" />
                                <circle cx="12" cy="10" r="3" />
                                <path d="M6.4 18.6a6 6 0 0 1 11.2 0" />
                            </svg>
                        </button>
                        <mat-menu #profile="matMenu" xPosition="before">
                            <div class="px-5">
                                <p class="ellipsis-line-1 text-lg font-medium text-[#004B65]">
                                    {{ userDetails.fullName }}
                                </p>
                                <p class="text-[#7995A0] font-light text-base ellipsis-line-1">
                                    {{ userDetails.email }}
                                </p>
                            </div>

                            <div class="h-[1px] w-full my-3 bg-[#CFE8FF]"></div>

                            <div class="px-5 py-1 space-y-5">
                                <a
                                    *ngFor="let item of SIDE_MENU_ITEMS"
                                    class="flex items-center"
                                    [id]="item.id ? item.id : null"
                                    [routerLink]="item.link"
                                    (click)="isMobileView && closeMobileMenu(true)">
                                    <mat-icon class="flex-shrink-0" [svgIcon]="item.icon"></mat-icon>
                                    <span class="font-medium text-[1.125rem] ml-2">
                                        {{ item.title | translate }}
                                    </span>
                                </a>
                            </div>

                            <div class="h-[1px] w-full my-3 bg-[#CFE8FF]"></div>

                            <!-- TODO: Integrate Logout API -->
                            <button type="button" class="px-5 py-2 text-error flex items-center" (click)="logout()">
                                <span class="inline-block bg-logout-icon icon-[1.25rem]"></span>
                                <span class="font-medium text-[1.125rem] ml-2">
                                    {{ 'menu.logout' | translate }}
                                </span>
                            </button>
                        </mat-menu>
                        <button
                            type="button"
                            class="lg:hidden leading-none"
                            (click)="isMobileMenuOpen = !isMobileMenuOpen">
                            <mat-icon>{{ isMobileMenuOpen ? 'close' : 'menu' }}</mat-icon>
                        </button>
                    }
                </div>
            </div>
        </div>
        <div *ngIf="!isAuthLayout && isMobileView" class="lg:hidden md:px-0 px-6 pb-4 flex items-center">
            <div class="search-bar w-full relative">
                <div class="form-control-wrapper flex items-center">
                    <mat-icon
                        svgIcon="search"
                        class="absolute left-3 !w-4 text-[#004B65] cursor-pointer"
                        (click)="onSearch(searchInputMobile.value)"></mat-icon>
                    <input
                        #searchInputMobile
                        id="globalSearchBar"
                        type="text"
                        [attr.maxlength]="SEARCH_MAX_LENGTH"
                        class="form-control form-textbox !px-8 !h-9"
                        [placeholder]="'placeholder.defaultSearchExample' | translate"
                        (keydown.enter)="onSearch(searchInputMobile.value)" />
                    <button
                        *ngIf="isSearchPopupVisible"
                        type="button"
                        class="flex items-center"
                        (click)="isSearchPopupVisible = false; onFilterClear()">
                        <mat-icon class="absolute right-3 text-[#a09d9d] !w-3" svgIcon="close"></mat-icon>
                    </button>
                </div>

                <div *ngIf="isSearchPopupVisible" #searchPopupMobile class="absolute top-[calc(100%+0.5rem)] w-full">
                    <app-general-search-dropdown
                        (filterClearClicked)="onFilterClear()"
                        (suggestedKeywordClicked)="onSuggestedKeyword($event)"
                        (hotTopicClicked)="onHotTopicClicked()"></app-general-search-dropdown>
                </div>
            </div>
            <!-- Same web flag as the desktop button: this is the web app's mobile breakpoint, not
                 the native app. The native app reads its own flag from the mobile API. -->
            <button
                *ngIf="isAskPodFeatureEnabled"
                type="button"
                id="askPodLogoMobile"
                class="ml-4 flex-shrink-0 md:hidden relative overflow-visible"
                aria-label="askPOD"
                [disabled]="isAskpodLoading || isAskpodDisabled"
                (click)="onAskPodClicked()">
                <img
                    class="h-6 w-auto"
                    src="./assets/images/askpod-logo.svg"
                    alt="askPOD" />
                <span class="askpod-new-badge">{{ 'askPodPopup.betaBadge' | translate }}</span>
            </button>
        </div>
        <div class="mobile-menu-container" [ngClass]="{ open: isMobileMenuOpen }">
            @if (!isAuthLayout) {
                <app-mobile-navbar (closeMobileMenu)="closeMobileMenu($event)"></app-mobile-navbar>
            }
        </div>
    </div>
</header>
